import React, { Fragment, useCallback, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet as RNStyleSheet,
  View,
} from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { Text, useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../../Config/constants';
import DateHelper from '../../../Helper/Date';
import useReservationBooking from '../../../Hooks/useReservationBooking';
import { useLazyGetListingQuery } from '../../../RTK/api/reservations';
import { TableDetails } from '../../../RTK/api/reservations/types';
import { pushRecentlySearch } from '../../../RTK/search';
import RecentlyViewedSection from '../../../Screens/Search/components/Recent';
import { TYPE } from '../../../Screens/Search/types';
import Service from '../../../Service';
import ThemeColor from '../../../Theme/colors';
import ThemeStyle from '../../../Theme/styles';

import SearchInput from '../../Search';
import { ReservationType } from '../../Sheets/BookReservation/types';

import SearchItem from './Item';

const initialState = {
  searchValue: '',
};

const SearchReservation = ({ size }: { size?: 'large' }) => {
  const searchRef = useRef<any>();
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigation = useNavigation();
  const { showBookingSchedulePicker, navigateToCheckoutDestination } =
    useReservationBooking();

  const [getListing, { data: eventsData, isLoading, isFetching, error }] =
    useLazyGetListingQuery();

  const recentlyReservationSearched = useSelector(
    (state: any) => state.search.recentlyReservationSearched,
  );

  const [isOpen, setOpen] = useState(false);
  const [state, setState] = useState(initialState);

  const divRef = useRef(null);
  const isLarge = size === 'large';

  const _onCuisinesOrRecentlyViewedPressed = async (val) => {
    searchRef.current._onInputChange(val, true);
    dispatch(pushRecentlySearch({ type: TYPE.RESERVATION, value: val }));
  };

  const onChangeText = (txt) => {
    if (txt.trim().length !== 0) {
      _apiCallForSearchContain(txt);
    } else {
      setState({
        searchValue: txt,
      });
    }
  };

  const _getOtherPayload = useCallback(() => {
    const today = dayjs();
    const currentNextTs = DateHelper.getToNextSlot(today);
    return {
      party_size: 1,
      date: currentNextTs.format(constants.DBDATE_FORMAT),
      time: currentNextTs.format(constants.DBTIME_FORMAT),
      limit: 10,
      offset: 0,
    };
  }, []);

  const _apiCallForSearchContain = (searchValue) => {
    setState({ searchValue });
    getListing({
      payload: {
        keyword: searchValue,
        ..._getOtherPayload(),
      },
    });
    dispatch(
      pushRecentlySearch({ type: TYPE.RESERVATION, value: searchValue }),
    );
  };

  const _open = () => {
    setOpen(true);
  };
  const _onClose = () => {
    setOpen(false);
    setState({ searchValue: '' });
  };

  const _onLayout = (e) => {
    if (e.nativeEvent.layout.width === 0) {
      _onClose();
    }
  };

  const _onBook = useCallback(
    async (store, table) => {
      const otherPayload = _getOtherPayload();
      const payload = {
        filter: {
          date: otherPayload.date,
          size: otherPayload.party_size,
          time: otherPayload.time,
        },
        itemInfo: { ...table, type: ReservationType.DINING },
        storeInfo: store,
      };
      const result = await showBookingSchedulePicker(payload);
      if (result?.success) {
        navigateToCheckoutDestination(navigation, result.data);
      }
    },
    [
      navigation,
      _getOtherPayload,
      showBookingSchedulePicker,
      navigateToCheckoutDestination,
    ],
  );

  const MemoizedSearchItem = useCallback(
    (props) => (
      <SearchItem
        {...props}
        onBook={(table: TableDetails) => _onBook(props.item.store, table)}
      />
    ),
    [_onBook],
  );

  return (
    <Fragment>
      <View ref={divRef} style={styles.searchInputWrapper} onLayout={_onLayout}>
        <View style={styles.searchInputContainer}>
          <View
            style={[ThemeStyle.spacingTopSmall, ThemeStyle.spacingBottomMedium]}
          >
            <SearchInput
              ref={searchRef}
              placeholder="Search events"
              onSearch={onChangeText}
              onFocus={_open}
              inputStyle={[ThemeStyle.pageBackground, styles.searchInput]}
              textStyle={[
                styles.textStyle,
                isLarge && ThemeStyle.pageVerticalSpacingMedium,
              ]}
              fill={theme['color-info-500']}
            />
          </View>
        </View>

        {(!lodashIsEmpty(recentlyReservationSearched) ||
          (!lodashIsEmpty(state.searchValue) &&
            !lodashIsEmpty(eventsData?.result))) &&
          isOpen && (
            <View style={[styles.contentContainer, isLarge && { top: 80 }]}>
              <FlatList
                dataSet={{ media: ids.searchContainer }}
                style={styles.searchContainer}
                data={
                  !lodashIsEmpty(state.searchValue) ? eventsData?.result : []
                }
                contentContainerStyle={styles.flatlist_search_container}
                renderItem={MemoizedSearchItem}
                ListHeaderComponentStyle={{ marginHorizontal: -10 }}
                ListHeaderComponent={
                  lodashIsEmpty(state.searchValue) && (
                    <View style={{ paddingHorizontal: 20 }}>
                      <RecentlyViewedSection
                        type={TYPE.RESERVATION}
                        onPress={_onCuisinesOrRecentlyViewedPressed}
                      />
                    </View>
                  )
                }
                ListEmptyComponent={
                  !lodashIsEmpty(state?.searchValue) && (
                    <View
                      style={[
                        ThemeStyle.alignItemsCenter,
                        ThemeStyle.justifyContentCenter,
                        { height: 100 },
                      ]}
                    >
                      <Text category="s1">
                        {!lodashIsEmpty(error)
                          ? 'An error has occurred'
                          : 'No results found.'}
                      </Text>
                      <Text category="p2" status="primary">
                        {!lodashIsEmpty(error)
                          ? Service.handleErrorMessage(error)
                          : "We couldn't find what you're looking for"}
                      </Text>
                    </View>
                  )
                }
                keyExtractor={(item) => item.store.id}
              />

              {(isLoading || isFetching) && (
                <View style={styles.loaderWrapper}>
                  <ActivityIndicator
                    size="large"
                    color={theme['color-info-500']}
                  />
                </View>
              )}
            </View>
          )}
      </View>

      {isOpen && (
        <View
          style={[styles.overlay, !isLarge && { position: 'fixed' }]}
          onClick={_onClose}
        />
      )}
    </Fragment>
  );
};

const { ids, styles } = StyleSheet.create({
  searchContainer: {
    ...ThemeStyle.flex1,
  },
  overlay: {
    ...RNStyleSheet.absoluteFillObject,
    left: -40,
    right: -40,
    zIndex: 99999,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  searchInput: {
    borderColor: ThemeColor.gray,
    borderRadius: 30,
    borderWidth: 1,
    minWidth: 200,
  },
  textStyle: { overflow: 'hidden', color: ThemeColor.black },
  contentContainer: {
    top: 60,
    left: 0,
    right: 0,
    width: 'auto',
    padding: 10,
    position: 'absolute',
    backgroundColor: ThemeColor.white,
    borderColor: ThemeColor.lightGray,
    ...ThemeStyle.shadow,
    borderWidth: 1,
    borderRadius: 10,
  },
  flatlist_search_container: {
    maxHeight: 600,
  },
  description_search_result: {
    marginLeft: 10,
    justifyContent: 'space-between',
    flexDirection: 'row',
    flex: 1,
  },
  loaderWrapper: {
    ...ThemeStyle.alignItemsCenter,
    ...ThemeStyle.justifyContentCenter,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
  },
  searchInputContainer: {
    ...ThemeStyle.pageBackground,
    backgroundColor: 'transparent',
  },
  searchInputWrapper: {
    zIndex: 999999999999999,
  },
});

export default React.memo(SearchReservation);
